<template>
  <div
    v-loading="!foreigner || !permit || !statuses || !documents"
    class="tw-flex-1"
  >
    <div class="tw-max-w-5xl tw-mx-auto">
      <div class="tw-flex tw-w-full tw-justify-between tw-items-center tw-mb-2">
        <router-link :to="backToPermitsLink">
          <i-button-icon-text
            size="medium"
            icon="icon-angle-left"
          >
            {{ $t('foreigner.permits.back_to_permits') }}
          </i-button-icon-text>
        </router-link>
        <div class="tw-flex tw-items-center">
          <i-user-avatar
            :avatar="foreigner?.avatar_url && foreigner?.avatar_url['128'] && `${$baseHost}/${foreigner?.avatar_url['128']}`"
            :fullname="foreigner?.fullname"
            size="mini"
            class="tw-mr-3"
          />
          <div class="tw-flex tw-flex-col tw-gap-1 tw-text-base tw-leading-5">
            <span class="tw-block tw-text-gray-dark dark:tw-text-dm-gray-dark tw-tracking-normal">
              {{ $t('foreigner.permits.belongs_to') }}
            </span>
            <span class="tw-inline-block tw-font-medium tw-tracking-wider">
              {{ foreigner?.fullname }}
            </span>
          </div>
        </div>
      </div>

      <h1 class="tw-leading-9 tw-mb-2">
        {{ permit?.permit_type_name }}
      </h1>

      <div class="tw-border-b tw-border-gray dark:tw-border-dm-gray tw-mb-6">
        <div class="tw-container tw-mx-auto">
          <el-tabs
            v-model="tab"
          >
            <el-tab-pane
              :label="$t('foreigners.permits.statuses')"
              name="status"
            />
            <el-tab-pane
              :label="$t('foreigners.permits.documents')"
              name="documents"
            />
          </el-tabs>
        </div>
      </div>

      <section
        v-if="tab === 'status'"
      >
        <IStepProgressWithLabels
          v-if="statuses.length > 0"
          :statuses="statuses"
          :active-status="activeStatus"
          :is-obtained="isObtained"
          class="tw-mb-6"
        />
        <ForeignerPermitStatus
          v-if="activeStatus"
          :status="activeStatus"
          :permit="permit"
          :closest-action-date="closestPermitActionDate"
          :current-status="currentStatus"
          class="tw-mb-3"
        />
      </section>

      <section v-if="tab === 'documents'">
        <ForeignerPermitDocument
          v-for="document in documents"
          :key="document.id"
          :document="document"
          :permit="permit"
          class="tw-mb-3"
        />
      </section>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import ForeignerPermitStatus from '@/components/Foreigner/Permits/ForeignerPermitStatus.vue';
import ForeignerPermitDocument from '@/components/Foreigner/Permits/ForeignerPermitDocument.vue';
import IStepProgressWithLabels from '@/components/ui/i-step-progress-with-labels.vue';
import { cloneDeep } from 'lodash';

export default {
  components: {
    IStepProgressWithLabels,
    ForeignerPermitStatus,
    ForeignerPermitDocument,
  },
  data() {
    return {
      tab: 'documents',
      activeStatus: null,
    };
  },
  computed: {
    ...mapState({
      me: (state) => state.v2.shared.profile.me,
      foreigner: (state) => state.v2.foreigner.foreigner.foreigner,
      permit: (state) => state.v2.foreigner.permits.permit,
      statuses: (state) => state.v2.foreigner.permits.statuses,
      documents: (state) => state.v2.foreigner.permits.documents,
    }),
    foreignerIdToLoad() {
      return this.$route.params.fid ?? this.$route.query.fid ?? this.foreigner?.id ?? this.me.id;
    },
    closestPermitActionDate() {
      const actionDates = cloneDeep(this.currentStatus?.action_dates ?? []);

      const today = this.$dayjs();
      let closestActionDate = null;
      let closestDiff = Infinity;
      actionDates.forEach((actionDate) => {
        const actionDateTime = this.$dayjs(`${actionDate.action_date} ${actionDate.action_time}`, 'DD.MM.YYYY HH:mm');
        const diff = actionDateTime.diff(today, 'minutes');
        const diffNormalized = Math.abs(diff);
        if (diffNormalized >= 0 && diff < closestDiff) {
          closestActionDate = actionDate;
          closestDiff = diffNormalized;
        }
      });

      return closestActionDate;
    },
    backToPermitsLink() {
      return this.permit == null || this.permit.foreigner_id === this.me.id ? '/' : `/foreigner/${this.permit.foreigner_id}`;
    },
    isObtained() {
      return this.permit.permit_status === 30;
    },
    currentStatus() {
      return this.statuses.find(({ is_current }) => Boolean(is_current));
    },
  },

  watch: {
    '$route.params.pid': {
      handler() {
        this.load();
      },
      immediate: true,
    },
    currentStatus() {
      if (this.currentStatus) {
        this.activeStatus = cloneDeep(this.currentStatus);
      }
    },
  },

  mounted() {
    this.load();

    const match = this.$route.path.match(/permits\/([^/]+)\/?(\w+)?/i);

    if (match) {
      const tabFromURL = match[2];

      if (['status', 'documents'].includes(tabFromURL)) {
        this.tab = tabFromURL;
      }
    } else {
      this.changeTab({ name: 'status' });
    }
  },

  methods: {
    load() {
      this.$store.dispatch('v2/foreigner/foreigner/load', this.foreignerIdToLoad);
      this.$store.dispatch('v2/foreigner/permits/loadPermit', { permitId: this.$route.params.pid, foreignerId: this.foreignerIdToLoad });
      this.$store.dispatch('v2/foreigner/permits/loadStatuses', this.$route.params.pid);
      this.$store.dispatch('v2/foreigner/permits/loadDocuments', this.$route.params.pid);
    },
  },
};
</script>
